/**
 * The main screen: what the household has, ordered by what needs using.
 */

import { useRef, useState } from 'preact/hooks';

import type { InventoryItem, Level } from '../api/types';
import type { Actions, Undoable } from '../actions';
import { dueWording, grouped } from '../domain/grouping';
import { cycled, isOutOfStock, levelWording, refills } from '../domain/levels';
import { isSlide, removesOnRelease, slideTo } from '../domain/swipe';

interface Props {
  items: InventoryItem[];
  actions: Actions;
  onChanged: (undoable: Undoable | null) => void;
}

export function Inventory({ items, actions, onChanged }: Props) {
  const now = new Date();
  const groups = grouped(items, now);

  if (groups.length === 0) {
    return (
      <p class="empty">
        Nothing here yet. Add the first thing with <strong>+</strong>.
      </p>
    );
  }

  return (
    <div class="groups">
      {groups.map((group) => (
        <section key={group.band} class={`group group-${group.band}`}>
          <h2>{group.heading}</h2>
          <ul>
            {group.items.map((item) => (
              <Row
                key={item.id}
                item={item}
                now={now}
                actions={actions}
                onChanged={onChanged}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

interface RowProps {
  item: InventoryItem;
  now: Date;
  actions: Actions;
  onChanged: (undoable: Undoable | null) => void;
}

function Row({ item, now, actions, onChanged }: RowProps) {
  const nextLevel: Level = cycled(item.level);
  const [slid, setSlid] = useState(0);
  const from = useRef<Travel | null>(null);

  // The tap that leaves an empty line puts stock back on it and restarts its shelf life, so
  // the button has to say that rather than naming the next rung.
  const fillsIt = refills(item, nextLevel);

  const cycle = async (): Promise<void> => {
    onChanged(await actions.cycleLevel(item, nextLevel));
  };

  const keep = async (): Promise<void> => {
    onChanged(await actions.keepLonger(item, 3));
  };

  const remove = async (): Promise<void> => {
    onChanged(await actions.removeItem(item));
  };

  const startTouch = (event: TouchEvent): void => {
    const touch = event.touches[0];
    from.current = touch === undefined ? null : { x: touch.clientX, y: touch.clientY };
  };

  const moveTouch = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (from.current === null || touch === undefined) {
      return;
    }

    const dx = touch.clientX - from.current.x;
    const dy = touch.clientY - from.current.y;

    // Once it is a scroll it stays a scroll: re-deciding mid-gesture is what makes a list
    // feel like it is fighting the finger.
    if (slid === 0 && !isSlide({ dx, dy })) {
      return;
    }

    setSlid(slideTo(dx));
  };

  const endTouch = (): void => {
    const travelled = slid;
    from.current = null;
    setSlid(0);

    if (removesOnRelease(travelled)) {
      void remove();
    }
  };

  return (
    <li class="row-track">
      {/* Revealed from under the row as it slides, so the gesture says what it will do
          before the finger lets go. */}
      <span class="row-verdict" aria-hidden="true">
        Remove
      </span>

      <div
        class="row"
        style={slid === 0 ? undefined : `transform: translateX(${slid}px)`}
        onTouchStart={startTouch}
        onTouchMove={moveTouch}
        onTouchEnd={endTouch}
        onTouchCancel={endTouch}
      >
        <div class="row-name">
          <span class="name">{item.name}</span>
          <span class="due">{dueWording(item, now)}</span>
        </div>

        {/* One tap changes the level, and the cycle wraps: the tap that empties a line is the
            same one that fills it again. */}
        <button
          type="button"
          class={`level level-${item.level}`}
          onClick={() => void cycle()}
          aria-label={
            fillsIt
              ? `${item.name} is ${levelWording(item.level)}. Restock it.`
              : `${item.name} is ${levelWording(item.level)}. Set to ${nextLevel}.`
          }
        >
          {levelWording(item.level)}
        </button>

        {/* A swipe is unreachable by keyboard and invisible to a screen reader, so the same
            act needs a control. Off-screen until focused rather than absent. */}
        <button type="button" class="offscreen" onClick={() => void remove()}>
          Remove {item.name}
        </button>

        {item.state === 'probably_gone' && !isOutOfStock(item) ? (
          // Asked once, and only once. Silence resolves it without anyone doing anything.
          <div class="asking">
            <span>Still have this?</span>
            <button type="button" onClick={() => void keep()}>
              Yes
            </button>
            <button type="button" onClick={() => void remove()}>
              No
            </button>
          </div>
        ) : null}
      </div>
    </li>
  );
}

/** Where a gesture began, so movement can be measured from it. */
interface Travel {
  x: number;
  y: number;
}
